import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import AccountSummaryCard from './components/AccountSummaryCard';
import QuickActionsPanel from './components/QuickActionsPanel';
import RecentTransactionsTable from './components/RecentTransactionsTable';
import ExchangeRateWidget from './components/ExchangeRateWidget';
import ComplianceAlertsPanel from './components/ComplianceAlertsPanel';

const Dashboard = () => {
  const [user, setUser] = useState(null);

  // Mock user data - in real app this would come from authentication context
  useEffect(() => {
    const mockUser = {
      id: 'user_123',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@email.com',
      role: 'customer',
      kycStatus: 'pending', // pending, under_review, verified, rejected
      accountLimits: {
        daily: { used: 2500, limit: 5000 },
        monthly: { used: 8750, limit: 25000 }
      },
      availableBalance: {
        USD: 12450.75,
        EUR: 3250.50,
        GBP: 1875.25
      }
    };
    setUser(mockUser);
  }, []);

  const handleLogout = () => {
    setUser(null);
    // In real app, clear authentication tokens
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const getKycStatusInfo = () => {
    switch (user?.kycStatus) {
      case 'verified':
        return {
          value: 'Verified',
          subtitle: 'Full account access',
          variant: 'success',
          iconColor: 'var(--color-success)'
        };
      case 'under_review':
        return {
          value: 'Under Review',
          subtitle: '1-2 business days',
          variant: 'warning',
          iconColor: 'var(--color-warning)'
        };
      case 'rejected':
        return {
          value: 'Action Required',
          subtitle: 'Resubmit documents',
          variant: 'error',
          iconColor: 'var(--color-error)'
        };
      default:
        return {
          value: 'Pending',
          subtitle: 'Complete verification',
          variant: 'warning',
          iconColor: 'var(--color-warning)'
        };
    }
  };

  const kycInfo = getKycStatusInfo();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Dashboard - MoneyTransfer Pro</title>
        <meta name="description" content="Manage your international money transfers, view account status, and access quick actions from your dashboard." />
      </Helmet>
      <Header user={user} onLogout={handleLogout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            Welcome back, {user?.name}
          </h1>
          <p className="text-muted-foreground">
            Manage your international transfers and account settings from your dashboard.
          </p>
        </div>

        {/* Account Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <AccountSummaryCard
            title="KYC Status"
            value={kycInfo?.value}
            subtitle={kycInfo?.subtitle}
            icon="Shield"
            iconColor={kycInfo?.iconColor}
            variant={kycInfo?.variant}
            actionLabel={user?.kycStatus !== 'verified' ? 'Complete' : null}
            onAction={user?.kycStatus !== 'verified' ? () => window.location.href = '/document-upload' : null}
          />

          <AccountSummaryCard
            title="Daily Limit"
            value={`$${user?.accountLimits?.daily?.used?.toLocaleString()}`}
            subtitle={`of $${user?.accountLimits?.daily?.limit?.toLocaleString()}`}
            icon="Calendar"
            progress={{
              used: `$${user?.accountLimits?.daily?.used?.toLocaleString()}`,
              limit: `$${user?.accountLimits?.daily?.limit?.toLocaleString()}`,
              percentage: (user?.accountLimits?.daily?.used / user?.accountLimits?.daily?.limit) * 100
            }}
          />

          <AccountSummaryCard
            title="Monthly Limit"
            value={`$${user?.accountLimits?.monthly?.used?.toLocaleString()}`}
            subtitle={`of $${user?.accountLimits?.monthly?.limit?.toLocaleString()}`}
            icon="CalendarDays"
            progress={{
              used: `$${user?.accountLimits?.monthly?.used?.toLocaleString()}`,
              limit: `$${user?.accountLimits?.monthly?.limit?.toLocaleString()}`,
              percentage: (user?.accountLimits?.monthly?.used / user?.accountLimits?.monthly?.limit) * 100
            }}
          />

          <AccountSummaryCard
            title="Available Balance"
            value={`$${user?.availableBalance?.USD?.toLocaleString()}`}
            subtitle={`€${user?.availableBalance?.EUR?.toLocaleString()} • £${user?.availableBalance?.GBP?.toLocaleString()}`}
            icon="Wallet"
            actionLabel="Add Funds"
            onAction={() => console.log('Add funds clicked')}
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <QuickActionsPanel />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Transactions */}
          <div className="lg:col-span-2">
            <RecentTransactionsTable />
          </div>

          {/* Right Column - Widgets */}
          <div className="space-y-8">
            <ExchangeRateWidget />
            <ComplianceAlertsPanel kycStatus={user?.kycStatus} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;